import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Typography,
  Container,
  Paper,
  CircularProgress,
  Alert,
  Grid,
} from "@mui/material";
import { GISProperty } from "../types/gis";
import GisMap from "./GisMap";

export default function GisDetail() {
  const { parcelId } = useParams<{ parcelId: string }>();
  const [property, setProperty] = useState<GISProperty | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const res = await fetch(`/api/gis/${parcelId}`);
        if (!res.ok) {
          throw new Error("Failed to fetch property details");
        }
        const data = await res.json();
        setProperty(data);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    if (parcelId) {
      fetchProperty();
    }
  }, [parcelId]);

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!property) {
    return <Alert severity="info">Property not found.</Alert>;
  }

  return (
    <Container>
      <Paper sx={{ mt: 4, p: 3 }}>
        <Typography variant="h4" gutterBottom>
          GIS Property Details
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6">
              Parcel ID: {property.parcel_id}
            </Typography>
            {property.site_address && (
              <Typography>
                <strong>Address:</strong> {property.site_address}
              </Typography>
            )}
            {property.owner_names && (
              <Typography>
                <strong>Owner:</strong> {property.owner_names.join(", ")}
              </Typography>
            )}
            {property.legal_description && (
              <Typography>
                <strong>Legal Description:</strong> {property.legal_description}
              </Typography>
            )}
            {property.area && (
              <Typography>
                <strong>Area:</strong> {property.area}{" "}
                {property.area_unit || ""}
              </Typography>
            )}
            {property.use_description && (
              <Typography>
                <strong>Use:</strong> {property.use_description}
              </Typography>
            )}
            {property.created_at && (
              <Typography>
                <strong>Added:</strong>{" "}
                {new Date(property.created_at).toLocaleString()}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {property.geometry ? (
              <GisMap
                properties={[property]}
                style={{ height: "400px", width: "100%" }}
              />
            ) : (
              <Typography>No map data available.</Typography>
            )}
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
}
